/** Split streaming markdown at its last block boundary: everything before the
 *  last blank line that sits outside an open code fence is settled (its blocks
 *  have closed and won't change), the rest is the open tail block. No boundary
 *  means the whole text is still the tail. */
export function splitSettledTail(markdown: string): { settled: string; tail: string } {
  let boundary = markdown.lastIndexOf('\n\n');
  while (boundary > 0) {
    if (fenceCount(markdown.slice(0, boundary)) % 2 === 0) {
      return { settled: markdown.slice(0, boundary), tail: markdown.slice(boundary + 2) };
    }
    boundary = markdown.lastIndexOf('\n\n', boundary - 1);
  }
  return { settled: '', tail: markdown };
}

function fenceCount(text: string): number {
  return (text.match(/^\s*```/gm) ?? []).length;
}

/** Drop inline markers the stream has opened but not yet closed — `**`, `*`,
 *  `` ` ``, `[` — so the tail renders as plain text until the closer lands
 *  instead of flashing literal asterisks/backticks/brackets. Code fences and
 *  bullet markers are not emphasis and are left alone. Heuristic by design:
 *  `_`, `~~` and a link's trailing `(` are not handled and show literally
 *  until the closing re-parse, and an odd `*` used for arithmetic drops the
 *  last emphasis opener instead — all transient and self-correcting. */
export function stripUnterminatedMarkers(tail: string): string {
  let text = dropLastIfOdd(tail, '**', tail);
  text = dropLastIfOdd(text, '*', text.replace(/\*\*/g, '  ').replace(/^(\s*)\*(\s)/gm, '$1 $2'));
  text = dropLastIfOdd(text, '`', text.replace(/```/g, '   '));
  if (count(text, '[') > count(text, ']')) {
    text = removeAt(text, text.lastIndexOf('['), 1);
  }
  return text;
}

function count(text: string, marker: string): number {
  return text.split(marker).length - 1;
}

/** Remove the last `marker` from `text` when `masked` (same length as `text`,
 *  with non-marker look-alikes blanked out) holds an odd number of them. */
function dropLastIfOdd(text: string, marker: string, masked: string): string {
  if (count(masked, marker) % 2 === 0) {
    return text;
  }
  return removeAt(text, masked.lastIndexOf(marker), marker.length);
}

function removeAt(text: string, index: number, length: number): string {
  return text.slice(0, index) + text.slice(index + length);
}
